import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule, MatRippleModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { FaIconModule } from '@libraries/fa';
import { scopeLoader } from '@libraries/transloco-root';
import { UiStateModule } from '@libraries/ui-state';
import { LetDirective, PushPipe } from '@ngrx/component';
import { ScrollToModule } from '@nicky-lenaers/ngx-scroll-to';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import {
  ConfirmationDialogComponent,
  FooterMobileComponent,
  PanelButtonComponent,
  SectionAnchorComponent,
  SectionHeaderComponent,
  SectionPanelComponent,
  SubsectionComponent,
} from './components';
import { AppHeaderComponent } from './components/app-header/app-header.component';
import { BaseLayoutComponent } from './components/base-layout/base-layout.component';
import { InfoTooltipComponent } from './components/info-tooltip/info-tooltip.component';
import { InformationBannerComponent } from './components/information-banner/app-information-banner.component';
import { LoadingPlaceholderComponent } from './components/loading-placeholder/loading-placeholder.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { NavigationSecondaryComponent } from './components/navigation-secondary/app-navigation-secondary.component';
import { PageButtonComponent } from './components/page-button/page-button.component';
import { ProgressStagesBarComponent } from './components/progress-stages-bar/progress-stages-bar.component';
import { SectionContentComponent } from './components/section-content/section-content.component';
import { SectionDescriptionComponent } from './components/section-description/section-description.component';
import { SectionErrorComponent } from './components/section-error/section-error.component';
import {
  DatePickerMaskDirective,
  DetectHeightExceedDirective,
  DisableFormAutocompleteDirective,
  DynamicHeightDirective,
  ElementDisabledDirective,
  HorizontalScrollShadowDirective,
  MatSelectInfiniteScrollDirective,
  NoShadowOnBottomDirective,
  NumberInputDirective,
  NumberWithLeadingZeroDirective,
  NumericRangeDirective,
  OnClickOutsideDirective,
  OutletActivationManagerDirective,
  PreloadImageDirective,
  ScrollToErrorsDirective,
} from './directives';
import { ShadowOnScrollDirective } from './directives/shadow-on-scroll.directive';
import { PhonePipe, TruncateWithHeightPipe } from './pipes';

const COMPONENTS = [
  AppHeaderComponent,
  BaseLayoutComponent,
  PageButtonComponent,
  SectionContentComponent,
  SectionErrorComponent,
  LoadingPlaceholderComponent,
  PanelButtonComponent,
  SectionAnchorComponent,
  ProgressStagesBarComponent,
  FooterMobileComponent,
  SectionDescriptionComponent,
  SectionHeaderComponent,
  SectionPanelComponent,
  SubsectionComponent,
  NavigationSecondaryComponent,
  InfoTooltipComponent,
  LoadingSpinnerComponent,
  ConfirmationDialogComponent,
  InformationBannerComponent,
];
const DIRECTIVES = [
  DatePickerMaskDirective,
  DisableFormAutocompleteDirective,
  ElementDisabledDirective,
  HorizontalScrollShadowDirective,
  NoShadowOnBottomDirective,
  NumberInputDirective,
  NumberWithLeadingZeroDirective,
  OnClickOutsideDirective,
  OutletActivationManagerDirective,
  PreloadImageDirective,
  ScrollToErrorsDirective,
  ShadowOnScrollDirective,
  DynamicHeightDirective,
  NumericRangeDirective,
  DetectHeightExceedDirective,
  MatSelectInfiniteScrollDirective,
];
const PIPES = [PhonePipe, TruncateWithHeightPipe];

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    MatDividerModule,
    MatExpansionModule,
    MatMenuModule,
    MatRippleModule,
    MatOptionModule,
    MatRadioModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    LetDirective,
    PushPipe,
    ScrollToModule.forRoot(),
    FaIconModule,
    UiStateModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatDialogModule,
    FormsModule,
    NgxMaskDirective,
    NgxMaskPipe,
  ],
  declarations: [...COMPONENTS, ...DIRECTIVES, ...PIPES],
  exports: [...COMPONENTS, ...DIRECTIVES, ...PIPES],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      multi: true,
      useValue: {
        scope: 'designSystem',
        loader: scopeLoader((lang: string, root: string) => import(`./${root}/${lang}.json`)),
      },
    },
    provideNgxMask(),
  ],
})
export class DesignSystemModule {}
